import { inject, Injectable, signal } from '@angular/core';
import { throwError } from 'rxjs';
import { AIAPIService } from './ai.api';
import { ChatMessage, WattsonQuestionContext } from './ai.models';

@Injectable({ providedIn: 'root' })
export class AIStore {
  private readonly api = inject(AIAPIService);

  public readonly messages = signal<ChatMessage[]>([]);
  public readonly isLoading = signal(false);

  public sendMessage(question: string, context: WattsonQuestionContext): void {
    if (!question.trim() || this.isLoading()) return;

    this.messages.update((msgs) => [...msgs, { role: 'user', text: question }]);
    this.isLoading.set(true);

    const loadingIndex = this.messages().length;
    this.messages.update((msgs) => [...msgs, { role: 'assistant', text: '', loading: true }]);

    const request$ = context.userId
      ? this.api.sendQuestion(
          question,
          context.userId,
          context.chargePointKind,
          context.chargePointName,
          context.pricePolicyCurrent,
          context.pricePolicyUpcoming,
        )
      : throwError(() => new Error('User profile is not available.'));

    request$.subscribe({
      next: (res) => {
        const answer = res.answer ?? '';
        this.messages.update((msgs) =>
          msgs.map((m, i) =>
            i === loadingIndex ? { role: 'assistant', text: answer, loading: false } : m,
          ),
        );
        this.isLoading.set(false);
      },
      error: (err) => {
        const errText = `Error: ${err?.message ?? 'Could not reach the service.'}`;
        this.messages.update((msgs) =>
          msgs.map((m, i) =>
            i === loadingIndex ? { role: 'assistant', text: errText, loading: false } : m,
          ),
        );
        this.isLoading.set(false);
      },
    });
  }

  public clearMessages(): void {
    this.api.resetSession();
    this.messages.set([]);
  }
}
